import styled from "styled-components";
import Popover from "common/Popover/Popover";
import React, { CSSProperties } from "react";
import { IconType } from "@shared/types";
import Tooltip from "common/Tooltips/Tooltip";

interface AvatarGroupProps {
  avatarsData: {
    key: string;
    title: string;
    text?: string;
    iconType?: IconType;
  }[];
  maxCount: number;
  totalCount?: number;
  AvatarComponent: React.FC<{
    text: string;
    style?: CSSProperties;
    size?: number;
    iconType?: IconType;
    iconSize?: number;
  }>;
  size?: number;
  style?: CSSProperties;
  hideTooltips?: boolean;
  iconSize?: number;
}

const AvatarGroup: React.FC<AvatarGroupProps> = ({
  avatarsData,
  maxCount,
  totalCount: propsTotalCount,
  AvatarComponent,
  size,
  style,
  hideTooltips,
  iconSize,
}) => {
  const totalCount = propsTotalCount ?? avatarsData.length;
  const popoverContent = avatarsData.slice(maxCount).map((avatarData) => {
    if (!avatarData.text) return;
    const avatar = (
      <AvatarWrapper>
        <AvatarComponent
          text={avatarData.text}
          size={size}
          iconSize={iconSize}
        />
      </AvatarWrapper>
    );
    if (hideTooltips) return avatar;
    return (
      <Tooltip key={avatarData.key} title={avatarData.title} placement="top">
        {avatar}
      </Tooltip>
    );
  });

  const numExtra = totalCount - maxCount;
  let extraCounter = (
    <ExchangesCounterWrapper $size={size} $isLong={numExtra > 9}>
      <ExchangesCounter $size={size}>+{numExtra}</ExchangesCounter>
    </ExchangesCounterWrapper>
  );
  if (!hideTooltips) {
    extraCounter = (
      <Popover
        placement="top"
        title={false}
        content={<FlexWrapper>{popoverContent}</FlexWrapper>}
        trigger="hover"
        classNames={{ root: "avatar" }}
      >
        {extraCounter}
      </Popover>
    );
  }

  return (
    <AvatarGroupWrapper style={style}>
      {avatarsData.slice(0, maxCount).map((avatarData) => {
        if (!avatarData.text) return;
        const avatar = (
          <AvatarComponent
            key={avatarData.key}
            iconType={avatarData.iconType}
            text={avatarData.text}
            style={{ marginLeft: -8 }}
            size={size}
            iconSize={iconSize}
          />
        );
        if (hideTooltips) return avatar;
        return (
          <Tooltip
            key={avatarData.key}
            title={avatarData.title}
            placement="top"
          >
            {avatar}
          </Tooltip>
        );
      })}
      {totalCount > maxCount && extraCounter}
    </AvatarGroupWrapper>
  );
};

export default AvatarGroup;

const AvatarGroupWrapper = styled.div`
  display: flex;
  margin-left: 8px;
`;
const ExchangesCounterWrapper = styled.div<{
  $size?: number;
  $isLong?: boolean;
}>`
  width: ${(p) => p.$size || 32}px;
  height: ${(p) => p.$size || 32}px;
  border-radius: 50%;
  ${(p) =>
    p.$isLong &&
    `
    width: auto;
    border-radius: 16px;
    padding: 0 6px;
  `};
  background: ${(p) => p.theme.colors.avatarBgColor};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: -8px;
  z-index: 1;
`;
const ExchangesCounter = styled.div<{ $size?: number }>`
  color: ${(p) => p.theme.colors.avatarColor};
  font-size: ${(p) => (p.$size && p.$size < 26 ? 10 : 14)}px;
  top: 1px;
  position: relative;
`;
const AvatarWrapper = styled.div`
  &:not(:first-child) {
    margin-left: 4px;
  }
`;
const FlexWrapper = styled.div`
  display: flex;
`;
